import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  getGoldReceiptByCase,
  getPledgeReleaseCaseById,
  generateFinalBill,
} from '../api';
import {
  Card, Typography, Row, Col, Spin, message, Space, Table, Divider,
  Alert, Button, Tag, Descriptions, Statistic, Modal, Result, Tooltip,
} from 'antd';
import {
  GoldOutlined, DollarOutlined, CheckCircleOutlined,
  ExclamationCircleOutlined, ArrowLeftOutlined,
  ArrowUpOutlined, ArrowDownOutlined, MinusOutlined, SwapOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { formatAmount4, calculateCaseRoundOff, formatRoundOff } from '../../../utils/goldCalculation';
import 'bootstrap/dist/css/bootstrap.min.css';

const { Title, Text } = Typography;

const formatPrice = (price) =>
  new Intl.NumberFormat('en-IN', { style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(price || 0);

const formatWholeAmount = (price) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(price || 0);

const formatRoundingAmount = (amount) => {
  const formatted = new Intl.NumberFormat('en-IN', { style: 'decimal', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Math.abs(amount || 0));
  if (amount > 0) {
    return `+₹ ${formatted}`;
  } else if (amount < 0) {
    return `-₹ ${formatted}`;
  }
  return `₹ 0`;
};

const GoldReceiptDetails = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const caseId = searchParams.get('caseId');

  const [caseData, setCaseData] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settling, setSettling] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  const loadData = useCallback(async () => {
    if (!caseId) return;
    setLoading(true);
    try {
      const [caseRes, receiptRes] = await Promise.all([
        getPledgeReleaseCaseById(caseId),
        getGoldReceiptByCase(caseId).catch(() => null),
      ]);
      setCaseData(caseRes.data);
      if (receiptRes?.data) setReceipt(receiptRes.data);
    } catch (err) {
      console.error('Error loading data:', err);
      messageApi.error('Failed to load gold receipt');
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── Settlement ──

  const handleSettle = () => {
    Modal.confirm({
      title: 'Generate Final Bill & Settle Case',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>This will:</p>
          <ul>
            <li>Generate an official <strong>Bill</strong> from the Gold Receipt</li>
            <li>
              Compute <strong>Net Gold Value</strong>:
              <ul style={{ marginTop: 4, marginBottom: 4 }}>
                <li>Gross Gold Value: ₹ {formatAmount4(receipt.finalGoldValue)}</li>
                {(receipt.processingFeePercent > 0 || receipt.processingFeeAmount > 0) && (
                  <li style={{ color: '#cf1322' }}>− Processing Fee ({receipt.processingFeePercent || 0}%): ₹ {formatAmount4(receipt.processingFeeAmount)}</li>
                )}
                {(receipt.roundingUpAmount || 0) !== 0 && (
                  <li style={{ color: (receipt.roundingUpAmount || 0) > 0 ? '#52c41a' : '#cf1322' }}>
                    {(receipt.roundingUpAmount || 0) > 0 ? '+ Rounding Up' : '− Rounding Down'}: {formatRoundingAmount(receipt.roundingUpAmount || 0)}
                  </li>
                )}
                {calculateCaseRoundOff(receipt.finalGoldValue, receipt.processingFeeAmount, receipt.roundingUpAmount, receipt.finalNetValue) !== 0 && (
                  <li style={{ color: '#8c8c8c' }}>Round Off (to nearest ₹): {formatRoundOff(calculateCaseRoundOff(receipt.finalGoldValue, receipt.processingFeeAmount, receipt.roundingUpAmount, receipt.finalNetValue))}</li>
                )}
                <li style={{ fontWeight: 'bold', color: '#3f8600' }}>
                  = Net Gold Value: ₹ {formatWholeAmount(receipt.finalNetValue)}
                </li>
              </ul>
            </li>
            <li>Compute <strong>settlement</strong> = Net Gold Value − Loan Paid − Customer Advance</li>
            <li>Compute <strong>profit</strong> = Net Gold Value − Capital Exposure</li>
          </ul>
          <p>This action will transition the case to <Tag color="magenta">BILL GENERATED</Tag>. This cannot be undone.</p>
        </div>
      ),
      okText: 'Generate Bill & Settle',
      okType: 'primary',
      cancelText: 'Cancel',
      onOk: async () => {
        setSettling(true);
        try {
          const result = await generateFinalBill(caseId);
          messageApi.success(`Bill ${result.data?.settlement?.billId || ''} generated — case settled`);
          await loadData();
        } catch (err) {
          console.error('Generate Final Bill error:', err);
          messageApi.error(err?.response?.data?.message || 'Failed to generate final bill');
        } finally {
          setSettling(false);
        }
      },
    });
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!caseId || !caseData) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <Alert type="error" message="Case not found or no case ID provided." showIcon />
      </div>
    );
  }

  if (!receipt) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <Result
          status="info"
          title="No Gold Receipt"
          subTitle="No gold receipt has been created for this case yet."
          extra={
            <Button onClick={() => navigate(`/pledge-release-cases/${caseId}`)}>
              Back to Case
            </Button>
          }
        />
      </div>
    );
  }

  const goldItems = receipt.finalGoldItems || [];
  const hasSettlement = caseData.finalSettlementAmount != null;
  const canSettle = caseData.status === 'VALUATION_COMPLETED';

  // Gold items read-only columns
  const columns = [
    { title: '#', width: 50, render: (_, __, idx) => idx + 1 },
    { title: 'Item Name', dataIndex: 'itemName', width: 140 },
    {
      title: 'Purity (%)',
      dataIndex: 'purity',
      width: 100,
      render: (v) => `${v}%`,
    },
    {
      title: 'Gross Wt. (gm)',
      dataIndex: 'grossWeight',
      width: 120,
      render: (v) => v?.toFixed(4),
    },
    {
      title: 'Stone/Wax Ded.',
      dataIndex: 'stoneWaxDeduction',
      width: 120,
      render: (v) => v?.toFixed(4),
    },
    {
      title: 'Dust Ded. (gm)',
      dataIndex: 'dustDeduction',
      width: 110,
      render: (v) => v?.toFixed(4),
    },
    {
      title: 'Net Wt. (gm)',
      dataIndex: 'netWeight',
      width: 110,
      render: (v) => <strong>{v?.toFixed(4)}</strong>,
    },
    {
      title: 'Final Rate/gm (₹)',
      dataIndex: 'finalRatePerGram',
      width: 140,
      render: (v) => `₹ ${formatPrice(v)}`,
    },
    {
      title: 'Total Price (₹)',
      dataIndex: 'totalPrice',
      width: 130,
      render: (v) => <strong>₹ {formatAmount4(v)}</strong>,
    },
  ];

  return (
    <>
      {contextHolder}
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            {/* Header */}
            <Card className="mb-3">
              <Row justify="space-between" align="middle">
                <Col>
                  <Space>
                    <Button
                      icon={<ArrowLeftOutlined />}
                      onClick={() => navigate(`/pledge-release-cases/${caseId}`)}
                    />
                    <Title level={3} style={{ margin: 0 }}>
                      <GoldOutlined style={{ marginRight: 8 }} />
                      Gold Receipt — {receipt.receiptId}
                    </Title>
                    <Tag color="gold">{caseData.caseId}</Tag>
                  </Space>
                </Col>
                <Col>
                  <Tag color={caseData.status === 'CLOSED' ? 'green' : 'blue'}>
                    {caseData.status?.replace(/_/g, ' ')}
                  </Tag>
                </Col>
              </Row>
            </Card>

            {/* Receipt Info */}
            <Card className="mb-3">
              <Descriptions title="Receipt Details" bordered column={{ xs: 1, sm: 2, md: 3 }}>
                <Descriptions.Item label="Receipt ID">{receipt.receiptId}</Descriptions.Item>
                <Descriptions.Item label="Valuation Date">
                  {receipt.valuationDate ? dayjs(receipt.valuationDate).format('DD MMM YYYY') : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Approved By">
                  {receipt.approvedBy?.fullName || receipt.approvedBy?.email || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Created">
                  {dayjs(receipt.createdAt).format('DD MMM YYYY, h:mm A')}
                </Descriptions.Item>
                <Descriptions.Item label="Remarks" span={2}>
                  {receipt.remarks || '-'}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            {/* Gold Items */}
            <Card title="Final Gold Items — Post Valuation" className="mb-3">
              <Table
                dataSource={goldItems.map((item, idx) => ({ ...item, key: idx }))}
                columns={columns}
                pagination={false}
                size="small"
                bordered
                scroll={{ x: 'max-content' }}
              />

              <Row gutter={16} className="mt-3">
                <Col xs={24} sm={6}>
                  <Card size="small" bordered>
                    <Statistic
                      title="Gross Gold Value"
                      value={receipt.finalGoldValue}
                      precision={4}
                      prefix="₹"
                    />
                  </Card>
                </Col>
                {(receipt.processingFeePercent > 0 || receipt.processingFeeAmount > 0) && (
                  <Col xs={24} sm={6}>
                    <Card size="small" bordered style={{ background: '#fff2f0' }}>
                      <Statistic
                        title={`Processing Fee (${receipt.processingFeePercent || 0}%)`}
                        value={receipt.processingFeeAmount || 0}
                        precision={4}
                        prefix="- ₹"
                        valueStyle={{ color: '#cf1322' }}
                      />
                    </Card>
                  </Col>
                )}
                {(receipt.roundingUpAmount || 0) !== 0 && (
                  <Col xs={24} sm={6}>
                    <Card size="small" bordered style={{ background: (receipt.roundingUpAmount || 0) > 0 ? '#f6ffed' : '#fff2f0' }}>
                      <Statistic
                        title="Rounding Up (₹)"
                        value={formatRoundingAmount(receipt.roundingUpAmount || 0)}
                        valueStyle={{ color: (receipt.roundingUpAmount || 0) > 0 ? '#52c41a' : '#cf1322', fontSize: 16 }}
                      />
                    </Card>
                  </Col>
                )}
                {calculateCaseRoundOff(receipt.finalGoldValue, receipt.processingFeeAmount, receipt.roundingUpAmount, receipt.finalNetValue) !== 0 && (
                  <Col xs={24} sm={6}>
                    <Card size="small" bordered>
                      <Statistic
                        title="Round Off (to nearest ₹)"
                        value={formatRoundOff(calculateCaseRoundOff(receipt.finalGoldValue, receipt.processingFeeAmount, receipt.roundingUpAmount, receipt.finalNetValue))}
                        valueStyle={{ color: '#8c8c8c', fontSize: 16 }}
                      />
                    </Card>
                  </Col>
                )}
                <Col xs={24} sm={6}>
                  <Card size="small" bordered style={{ background: '#f6ffed' }}>
                    <Statistic
                      title="Final Net Value"
                      value={receipt.finalNetValue}
                      precision={0}
                      prefix="₹"
                      valueStyle={{ color: '#3f8600' }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={6}>
                  <Card size="small" bordered>
                    <Statistic
                      title="Final Net Weight"
                      value={receipt.finalTotalNetWeight}
                      precision={4}
                      suffix="gm"
                    />
                  </Card>
                </Col>
              </Row>
            </Card>

            {/* ── Pre vs Post Valuation Comparison ── */}
            {(() => {
              // Pre-valuation (estimated) values from the case
              const preGrossWt = caseData.totalGrossWeight || 0;
              const preNetWt = caseData.totalNetWeight || 0;
              const preGrossValue = caseData.estimatedTotalAmount || 0;
              const preNetValue = caseData.approxFinalAmount || 0;
              const preAvgRate = preNetWt > 0 ? preGrossValue / preNetWt : 0;
              const preTotalPrice = preGrossValue;

              // Post-valuation (actual) values from the receipt
              const postGrossWt = receipt.finalTotalGrossWeight || 0;
              const postNetWt = receipt.finalTotalNetWeight || 0;
              const postGrossValue = receipt.finalGoldValue || 0;
              const postNetValue = receipt.finalNetValue || 0;
              const postAvgRate = postNetWt > 0 ? postGrossValue / postNetWt : 0;
              const postTotalPrice = postGrossValue;

              // Financial fields — pre uses estimated, post uses actual
              const closureAmount = caseData.closureAmount || 0;
              const totalLoanPaid = caseData.totalLoanPaid || 0;
              const totalCustomerAdvancePaid = caseData.totalCustomerAdvancePaid || 0;
              const capitalExposure = caseData.capitalExposure || 0;

              const preRemainingLoan = closureAmount - totalLoanPaid;
              const postRemainingLoan = preRemainingLoan; // doesn't change with valuation

              const preTakeHome = preNetValue - closureAmount;
              const postTakeHome = postNetValue - closureAmount;

              const preRemainingTakeHome = preTakeHome - totalCustomerAdvancePaid;
              const postRemainingTakeHome = postTakeHome - totalCustomerAdvancePaid;

              const diffTag = (diff, unit = '', precision = 2, exact = false) => {
                if (Math.abs(diff) < 0.005) {
                  return <Tag><MinusOutlined /> No change</Tag>;
                }
                const isPositive = diff > 0;
                return (
                  <Tag color={isPositive ? 'green' : 'red'}>
                    {isPositive ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                    {' '}{isPositive ? '+' : ''}{unit === '₹' ? (exact ? `₹ ${formatAmount4(Math.abs(diff))}` : formatWholeAmount(Math.abs(diff))) : `${diff.toFixed(precision)} ${unit}`}
                  </Tag>
                );
              };

              const goldCompareData = [
                { key: 'grossWt', label: 'Gross Weight', pre: preGrossWt, post: postGrossWt, unit: 'gm', precision: 4 },
                { key: 'netWt', label: 'Net Weight', pre: preNetWt, post: postNetWt, unit: 'gm', precision: 4 },
                { key: 'grossVal', label: 'Gross Value', pre: preGrossValue, post: postGrossValue, unit: '₹', precision: 2, exact: true },
                { key: 'netVal', label: 'Net Value', pre: preNetValue, post: postNetValue, unit: '₹', precision: 2 },
                { key: 'rate', label: 'Est. Rate/gm', pre: preAvgRate, post: postAvgRate, unit: '₹', precision: 2 },
                { key: 'total', label: 'Total Price', pre: preTotalPrice, post: postTotalPrice, unit: '₹', precision: 2, exact: true },
              ];

              const preRoundingUpAmount = caseData.roundingUpAmount || 0;
              const postRoundingUpAmount = receipt.roundingUpAmount || 0;

              const finCompareData = [
                { key: 'closure', label: 'Closure Amount', pre: closureAmount, post: closureAmount, unit: '₹', precision: 2 },
                { key: 'loanPaid', label: 'Total Loan Paid', pre: totalLoanPaid, post: totalLoanPaid, unit: '₹', precision: 2 },
                { key: 'remainLoan', label: 'Remaining Loan Balance', pre: preRemainingLoan, post: postRemainingLoan, unit: '₹', precision: 2 },
                { key: 'rounding', label: 'Rounding Up (₹)', pre: preRoundingUpAmount, post: postRoundingUpAmount, unit: '₹', precision: 2, isRounding: true },
                { key: 'takeHome', label: 'Take-Home Eligible', pre: preTakeHome, post: postTakeHome, unit: '₹', precision: 2, highlight: true },
                { key: 'custAdv', label: 'Customer Advance Paid', pre: totalCustomerAdvancePaid, post: totalCustomerAdvancePaid, unit: '₹', precision: 2 },
                { key: 'remainTakeHome', label: 'Remaining Take-Home', pre: preRemainingTakeHome, post: postRemainingTakeHome, unit: '₹', precision: 2, highlight: true },
                { key: 'capExp', label: 'Capital Exposure', pre: capitalExposure, post: capitalExposure, unit: '₹', precision: 2 },
              ];

              const compareColumns = [
                {
                  title: 'Field',
                  dataIndex: 'label',
                  key: 'label',
                  width: 180,
                  render: (text, row) => (
                    <Text strong={!!row.highlight}>{text}</Text>
                  ),
                },
                {
                  title: 'Estimated (Pre)',
                  dataIndex: 'pre',
                  key: 'pre',
                  width: 150,
                  align: 'right',
                  render: (v, row) => {
                    if (row.isRounding) {
                      return <Text style={{ color: v > 0 ? '#52c41a' : v < 0 ? '#cf1322' : '#000' }}>{formatRoundingAmount(v)}</Text>;
                    }
                    return row.unit === '₹' ? (row.exact ? `₹ ${formatAmount4(v)}` : formatWholeAmount(v)) : v.toFixed(row.precision) + (row.unit !== '₹' ? ` ${row.unit}` : '');
                  },
                },
                {
                  title: 'Actual (Post)',
                  dataIndex: 'post',
                  key: 'post',
                  width: 150,
                  align: 'right',
                  render: (v, row) => (
                    <Text strong style={{ color: row.isRounding && v !== 0 ? (v > 0 ? '#52c41a' : '#cf1322') : 'inherit' }}>
                      {row.isRounding ? formatRoundingAmount(v) : (row.unit === '₹' ? (row.exact ? `₹ ${formatAmount4(v)}` : formatWholeAmount(v)) : v.toFixed(row.precision) + (row.unit !== '₹' ? ` ${row.unit}` : ''))}
                    </Text>
                  ),
                },
                {
                  title: 'Difference',
                  key: 'diff',
                  width: 180,
                  align: 'center',
                  render: (_, row) => diffTag(row.post - row.pre, row.unit, row.precision, row.exact),
                },
              ];

              return (
                <Card
                  title={
                    <Space>
                      <SwapOutlined />
                      Pre vs Post Valuation Comparison
                    </Space>
                  }
                  className="mb-3"
                >
                  <Title level={5} style={{ marginBottom: 12 }}>Gold Details</Title>
                  <Table
                    dataSource={goldCompareData}
                    columns={compareColumns}
                    pagination={false}
                    size="small"
                    bordered
                  />

                  <Divider />

                  <Title level={5} style={{ marginBottom: 12 }}>Financial Summary</Title>
                  <Table
                    dataSource={finCompareData}
                    columns={compareColumns}
                    pagination={false}
                    size="small"
                    bordered
                    rowClassName={(row) => row.highlight ? 'ant-table-row-highlight' : ''}
                  />
                </Card>
              );
            })()}

            {/* Gold Images */}
            {receipt.goldImages && receipt.goldImages.length > 0 && (
              <Card title="Gold Images" className="mb-3">
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  {receipt.goldImages.map((img, idx) => (
                    <a key={img.fileId || idx} href={img.url} target="_blank" rel="noopener noreferrer">
                      <img
                        src={img.thumbnailUrl || img.url}
                        alt={img.name || `Gold image ${idx + 1}`}
                        style={{
                          width: 140,
                          height: 140,
                          objectFit: 'cover',
                          borderRadius: 8,
                          border: '1px solid #d9d9d9',
                          cursor: 'pointer',
                        }}
                      />
                    </a>
                  ))}
                </div>
              </Card>
            )}

            {/* Settlement Section */}
            <Card
              title={
                <Space>
                  <DollarOutlined />
                  Settlement
                </Space>
              }
              className="mb-3"
              extra={
                canSettle && !hasSettlement ? (
                  <Button
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    loading={settling}
                    onClick={handleSettle}
                  >
                    Compute Settlement
                  </Button>
                ) : null
              }
            >
              {hasSettlement ? (
                <>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={6}>
                      <Card size="small" bordered style={{ background: '#f6ffed' }}>
                        <Statistic
                          title="Final Net Value"
                          value={caseData.finalNetValue}
                          precision={2}
                          prefix="₹"
                          valueStyle={{ color: '#3f8600' }}
                        />
                      </Card>
                    </Col>
                    <Col xs={24} sm={6}>
                      <Card size="small" bordered>
                        <Statistic
                          title="Settlement Amount"
                          value={caseData.finalSettlementAmount}
                          precision={2}
                          prefix="₹"
                          valueStyle={{
                            color: caseData.finalSettlementAmount >= 0 ? '#3f8600' : '#cf1322',
                          }}
                        />
                      </Card>
                    </Col>
                    <Col xs={24} sm={6}>
                      <Card size="small" bordered>
                        <Statistic
                          title="Actual Profit"
                          value={caseData.actualProfit}
                          precision={2}
                          prefix="₹"
                          valueStyle={{
                            color: caseData.actualProfit >= 0 ? '#3f8600' : '#cf1322',
                          }}
                        />
                      </Card>
                    </Col>
                    <Col xs={24} sm={6}>
                      <Card size="small" bordered>
                        <Statistic
                          title="Profit %"
                          value={caseData.profitPercentage}
                          precision={2}
                          suffix="%"
                          valueStyle={{
                            color: caseData.profitPercentage >= 0 ? '#3f8600' : '#cf1322',
                          }}
                        />
                      </Card>
                    </Col>
                  </Row>

                  <Divider />

                  <Descriptions bordered size="small" column={{ xs: 1, sm: 2 }}>
                    <Descriptions.Item label="Closure Amount">
                      ₹ {formatPrice(caseData.closureAmount)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Total Loan Paid">
                      ₹ {formatPrice(caseData.totalLoanPaid)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Customer Advance Paid">
                      ₹ {formatPrice(caseData.totalCustomerAdvancePaid)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Gross Gold Value">
                      ₹ {formatAmount4(caseData.finalGoldValue)}
                    </Descriptions.Item>
                    {(receipt?.processingFeeAmount || 0) > 0 && (
                      <Descriptions.Item label="Processing Fee">
                        <Text type="danger">- ₹ {formatAmount4(receipt.processingFeeAmount)}</Text>
                      </Descriptions.Item>
                    )}
                    {(receipt?.roundingUpAmount || 0) !== 0 && (
                      <Descriptions.Item label="Rounding Up">
                        {formatRoundingAmount(receipt.roundingUpAmount)}
                      </Descriptions.Item>
                    )}
                    {receipt && calculateCaseRoundOff(receipt.finalGoldValue, receipt.processingFeeAmount, receipt.roundingUpAmount, receipt.finalNetValue) !== 0 && (
                      <Descriptions.Item label="Round Off (to nearest ₹)">
                        <Text type="secondary">{formatRoundOff(calculateCaseRoundOff(receipt.finalGoldValue, receipt.processingFeeAmount, receipt.roundingUpAmount, receipt.finalNetValue))}</Text>
                      </Descriptions.Item>
                    )}
                    <Descriptions.Item label="Final Net Value">
                      <Text strong type="success">₹ {formatPrice(caseData.finalNetValue)}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Final Settlement">
                      <Text
                        strong
                        type={caseData.finalSettlementAmount >= 0 ? 'success' : 'danger'}
                      >
                        ₹ {formatPrice(caseData.finalSettlementAmount)}
                      </Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Case Status">
                      <Tag color={['PAYMENT_SETTLED', 'BILL_GENERATED', 'CLOSED'].includes(caseData.status) ? 'green' : 'blue'}>
                        {caseData.status?.replace(/_/g, ' ')}
                      </Tag>
                    </Descriptions.Item>
                  </Descriptions>
                </>
              ) : canSettle ? (
                <Alert
                  type="info"
                  showIcon
                  message="Settlement Not Yet Computed"
                  description="Click 'Compute Settlement' to calculate the final settlement including profit, settlement amount, and profit percentage."
                />
              ) : (
                <Alert
                  type="warning"
                  showIcon
                  message="Settlement Not Available"
                  description={`The case must be in VALUATION COMPLETED status to compute settlement. Current: ${caseData.status?.replace(/_/g, ' ')}`}
                />
              )}
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

export default GoldReceiptDetails;
